import { useEffect, useState } from "react";
import { Button, Container, Row, Col, Spinner } from "reactstrap";
import requests from "../requests";

const Footer = () => {
  const [asOf, setAsOf] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [subMsg, setSubMsg] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (subStatus === "loading") return;
    const value = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setSubStatus("error");
      setSubMsg("Please enter a valid email address.");
      return;
    }
    setSubStatus("loading");
    setSubMsg("");
    try {
      await requests.post("/api/email/subscribe", { email: value, source: "footer" });
      setSubStatus("ok");
      setSubMsg("You're subscribed — watch your inbox for market insights.");
      setEmail("");
    } catch {
      setSubStatus("error");
      setSubMsg("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    requests.get("/api/stats")
      .then(r => {
        const raw = (r.data as { data_as_of?: string | null }).data_as_of ?? null;
        // Render YYYY-MM-DD as "Jun 18, 2026"; anchor at noon to avoid TZ slips.
        if (raw) {
          const d = new Date(`${raw}T12:00:00`);
          setAsOf(Number.isNaN(d.getTime())
            ? raw
            : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }));
        }
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  return (
    <footer className="site-footer">
      <Container>
        <Row>
          <Col md="4">
            <div className="footer-brand">
              <span className="logo-text">DEJA</span>
              <span className="logo-chart">CHART</span>
            </div>
            <p>Find the charts that looked like this one, and see what happened next. A research tool, not investment advice.</p>
          </Col>
          <Col md="2">
            <h4>Company</h4>
            <ul className="footer-links">
              <li>
                <a href="/about">About</a>
              </li>
              <li>
                <a href="/pricing">Pricing</a>
              </li>
              <li>
                <a href="/contact">Contact</a>
              </li>
            </ul>
          </Col>
          <Col md="2">
            <h4>Resources</h4>
            <ul className="footer-links">
              <li>
                <a href="/stocks">Browse Stocks</a>
              </li>
              <li>
                <a href="/featured">Featured Matches</a>
              </li>
              <li>
                <a href="/game">Call the Chart</a>
              </li>
              <li>
                <a href="/about#faq">FAQ</a>
              </li>
              <li>
                <a href="/developers">API</a>
              </li>
            </ul>
          </Col>
          <Col md="4">
            <h4>Stay Updated</h4>
            <p>
              Subscribe to receive our latest market insights and pattern
              alerts.
            </p>
            <form className="email-signup" onSubmit={handleSubscribe}>
              <input
                type="email"
                placeholder="Your email address"
                className="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email address"
                disabled={subStatus === "loading"}
              />
              <Button className="subscribe-btn" type="submit" disabled={subStatus === "loading"}>
                {subStatus === "loading" ? <Spinner size="sm" /> : "Subscribe"}
              </Button>
            </form>
            {subMsg && (
              <p
                role="status"
                style={{
                  marginTop: 8,
                  fontSize: "0.85rem",
                  color: subStatus === "ok" ? "var(--success, #16a34a)" : "var(--danger, #ef4444)",
                }}
              >
                {subMsg}
              </p>
            )}
          </Col>
        </Row>
        <hr />
        <Row className="copyright-row">
          <Col>
            <p className="copyright">
              © {new Date().getFullYear()} DejaChart. All rights reserved.
              {asOf && <span style={{ marginLeft: 12, opacity: 0.7 }}>· Data as of {asOf}</span>}
            </p>
          </Col>
          <Col className="text-right">
            <div className="footer-legal">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
            </div>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export default Footer;
